import React, { forwardRef, useState } from 'react';
import '../css/ContactForm.css';
import { db } from '../firebaseConfig';
import { addDoc, collection } from 'firebase/firestore';

const ContactForm = forwardRef((props, ref) => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
    if (submitError) {
      setSubmitError('');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      await addDoc(collection(db, 'siteInquiries'), {
        ...formData,
        timestamp: new Date().toISOString()
      });

      setFormSubmitted(true);
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      setSubmitError('Failed to send your message. Please try again or contact us directly at info@gleasondevelopment.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-form" ref={ref}>
      {formSubmitted ? (
        <div className="success-message" role="alert" aria-live="polite">
          <h2>Thank you for your submission!</h2>
          <p>We will get back to you as soon as possible</p>
          <button
            onClick={() => setFormSubmitted(false)}
            style={{ marginTop: 'var(--space-6)' }}
          >
            Send Another Message
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          <p>Please use the form below to contact us with any inquiries</p>

          {submitError && (
            <div className="form-error" role="alert" aria-live="assertive">
              {submitError}
            </div>
          )}

          <label htmlFor="name">
            Name:
            <input
              id="name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? "name-error" : undefined}
              required
              minLength={2}
              maxLength={100}
              disabled={isSubmitting}
            />
            {errors.name && (
              <span id="name-error" className="field-error" role="alert">
                {errors.name}
              </span>
            )}
          </label>

          <label htmlFor="email">
            Email:
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? "email-error" : undefined}
              required
              maxLength={200}
              disabled={isSubmitting}
            />
            {errors.email && (
              <span id="email-error" className="field-error" role="alert">
                {errors.email}
              </span>
            )}
          </label>

          <label htmlFor="message">
            Message:
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              aria-invalid={!!errors.message}
              aria-describedby={errors.message ? "message-error" : undefined}
              required
              minLength={10}
              maxLength={2000}
              disabled={isSubmitting}
            ></textarea>
            {errors.message && (
              <span id="message-error" className="field-error" role="alert">
                {errors.message}
              </span>
            )}
          </label>

          <button
            type="submit"
            disabled={isSubmitting}
            className={isSubmitting ? 'loading' : ''}
            aria-busy={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Submit'}
          </button>
        </form>
      )}
    </div>
  );
});

export default ContactForm;
